import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Slider,
  FormHelperText,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  setOnline,
  setFaceToFace,
  setDistance,
  setPosition,
  setPrice,
} from "../../../store/setProfile";
import PlaceSelect from "../../../Components/PlaceSelect/PlaceSelect";

export default function TutoringOptions({ onDirtyChange, onValidChange, onSave }: any) {
  const dispatch = useDispatch();
  const { online, faceToFace, distance, postcode, latitude, longitude, price } =
    useSelector((state: any) => state.profile.information);
  const [postcodeError, setPostcodeError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const minDistance = 0;
  const priceMarks = [
    { value: 15, label: "£15" },
    { value: 100, label: "£100+" },
  ];

  const setNewPrice = (event: any, newPrice: number | number[], activeThumb?: number) => {
    if (!Array.isArray(newPrice)) return;
    
    if (activeThumb === 0) {
      dispatch(
        setPrice([Math.min(newPrice[0], (price?.[1] || 100) - minDistance), price?.[1] || 100])
      );
    } else if (activeThumb === 1) {
      dispatch(
        setPrice([price?.[0] || 15, Math.max(newPrice[1], (price?.[0] || 15) + minDistance)])
      );
    }
    onDirtyChange?.(true);
  };

  const valuetext = (value: number) => `£${value}`;

  useEffect(() => {
    onDirtyChange?.(false);
  }, []);

  useEffect(() => {
    // Notify parent of validation status
    const isValid = online || faceToFace;
    onValidChange?.(isValid);
  }, [online, faceToFace, onValidChange]);

  const handleOnlineChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setOnline(event.target.checked));
    setTouched(true);
    onDirtyChange?.(true);
  };

  const handleFaceToFaceChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(setFaceToFace(event.target.checked));
    setTouched(true);
    onDirtyChange?.(true);
    if (!event.target.checked) {
      // Reset postcode-related fields when faceToFace is unchecked
      dispatch(
        setPosition({
          postcode: "",
          latitude: -1,
          longitude: -1,
          address: "",
          addressDetails: {},
        })
      );
      setPostcodeError(null);
    }
  };

  const handleDistanceChange = (_: Event, newValue: number | number[]) => {
    dispatch(setDistance(newValue as number));
    onDirtyChange?.(true);
  };

  const handlePlaceSelect = (event: any) => {
    const place = event.target.value;
    if (!place) return;
    setPostcodeError(place.error || null);
    dispatch(
      setPosition({
        latitude: place.latitude,
        longitude: place.longitude,
        address: place.address,
        addressDetails: place.addressDetails || {},
        postcode: place.postcode,
      })
    );
    onDirtyChange?.(true);
  };

  (TutoringOptions as any).save = async () => {
    // Save is handled by Redux state
    onDirtyChange?.(false);
  };

  return (
    <Box>
      <Typography 
        variant="h2" 
        sx={{ 
          fontWeight: 700,
        }}
      >
        <Box sx={{ color: '#712d94 !important' }}>
          Lesson preferences
        </Box>
      </Typography>
      <Box 
        sx={{ 
          '&': {
            marginBottom: '1.5rem !important'
          }
        }}
      >
        <Typography variant="body2" sx={{ color: "#6b7280" }}>
          (At least one option is required)
        </Typography>
      </Box>

      <Card
        elevation={0}
        sx={{
          border: "1px solid #e9e9ec",
          borderRadius: 2,
          mb: 3,
        }}
      >
        <CardContent>
          <FormControl fullWidth error={touched && !online && !faceToFace}>
            <FormLabel
              id="tutoring-options-label"
              sx={{ mb: 2, fontWeight: 600, fontSize: "1rem" }}
            >
              Your private tutoring options
            </FormLabel>
            <FormGroup row sx={{ justifyContent: "flex-start", gap: 4 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={online || false}
                    onChange={handleOnlineChange}
                    size="medium"
                    sx={{
                      color: "#712d94",
                      "&.Mui-checked": {
                        color: "#712d94",
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: "1rem", fontWeight: 500 }}>
                    Online
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={faceToFace || false}
                    onChange={handleFaceToFaceChange}
                    size="medium"
                    sx={{
                      color: "#712d94",
                      "&.Mui-checked": {
                        color: "#712d94",
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: "1rem", fontWeight: 500 }}>
                    In Person
                  </Typography>
                }
              />
            </FormGroup>
            {touched && !online && !faceToFace && (
              <FormHelperText>Please select at least one tutoring option</FormHelperText>
            )}
          </FormControl>

          {faceToFace && (
            <>
              <Divider sx={{ my: 3 }} />
              
              <FormControl fullWidth sx={{ mb: 3 }}>
                <FormLabel
                  id="postcode-label"
                  sx={{ mb: 1, fontWeight: 600, fontSize: "1rem" }}
                >
                  Postcode (UK only) <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <PlaceSelect
                  value={postcode}
                  onChange={handlePlaceSelect}
                  error={postcodeError}
                />
                <FormHelperText error={!!postcodeError} sx={{ mt: 1 }}>
                  {postcodeError ||
                    "Your full address will never be shown. Only area e.g. London, Muswell Hill"}
                </FormHelperText>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 4 }}>
                <FormLabel
                  id="distance-label"
                  sx={{ mb: 2, fontWeight: 600, fontSize: "1rem", color: "#1a1a1a" }}
                >
                  How far are you willing to travel?
                </FormLabel>
                <Box sx={{ px: 2, pb: 1 }}>
                  <Slider
                    aria-label="distance"
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${value} miles`}
                    step={1}
                    marks={[
                      { value: 0, label: '0' },
                      { value: 10, label: '10' },
                      { value: 20, label: '20' },
                      { value: 30, label: '30' }
                    ]}
                    min={0}
                    max={30}
                    value={distance || 0}
                    onChange={handleDistanceChange}
                    sx={{
                      color: "#712d94",
                      height: 6,
                      "& .MuiSlider-thumb": {
                        width: 20,
                        height: 20,
                        "&:hover": {
                          boxShadow: "0 0 0 8px rgba(113, 45, 148, 0.16)",
                        },
                      },
                      "& .MuiSlider-track": {
                        height: 6,
                      },
                      "& .MuiSlider-rail": {
                        height: 6,
                        opacity: 0.3,
                      },
                      "& .MuiSlider-markLabel": {
                        fontSize: "0.75rem",
                        color: "#64748b",
                        fontWeight: 500,
                      },
                      "& .MuiSlider-mark": {
                        backgroundColor: "#cbd5e1",
                        height: 8,
                        width: 2,
                      },
                    }}
                  />
                </Box>
                <FormHelperText sx={{ mt: 0.5, fontSize: "0.875rem", color: "#64748b", px: 2 }}>
                  Currently set to {distance || 0} miles from your location
                </FormHelperText>
              </FormControl>

              <FormControl fullWidth>
                <FormLabel
                  id="price-label"
                  sx={{ mb: 2, fontWeight: 600, fontSize: "1rem", color: "#1a1a1a" }}
                >
                  What is your hourly rate?
                </FormLabel>
                <Box sx={{ px: 2, pb: 1 }}>
                  <Slider
                    value={price || [15, 100]}
                    onChange={setNewPrice}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `£${value}`}
                    getAriaValueText={valuetext}
                    min={15}
                    max={100}
                    marks={priceMarks}
                    sx={{
                      color: "#712d94",
                      height: 6,
                      "& .MuiSlider-thumb": {
                        width: 20,
                        height: 20,
                        "&:hover": {
                          boxShadow: "0 0 0 8px rgba(113, 45, 148, 0.16)",
                        },
                      },
                      "& .MuiSlider-track": {
                        height: 6,
                      },
                      "& .MuiSlider-rail": {
                        height: 6,
                        opacity: 0.3,
                      },
                      "& .MuiSlider-markLabel": {
                        fontSize: "0.75rem",
                        color: "#64748b",
                        fontWeight: 500,
                      },
                      "& .MuiSlider-mark": {
                        backgroundColor: "#cbd5e1",
                        height: 8,
                        width: 2,
                      },
                    }}
                  />
                </Box>
                <FormHelperText sx={{ mt: 0.5, fontSize: "0.875rem", color: "#64748b", px: 2 }}>
                  Currently set to £{price?.[0] || 15} - £{price?.[1] || 100} per hour
                </FormHelperText>
              </FormControl>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
